'use client';

import { useRouter } from 'next/navigation';
import { KeyRound, Store } from 'lucide-react';
import { Avatar, Button } from '@mokoboko/components';

import { PageState, PageStateOwnerCard } from '@/components/page-state';
import {
  GIFT_CODE_STATE_COPY,
  type GiftCodeClosedState,
} from '@/helpers/gift-card';
import { BOOKING_PAGE_UNAVAILABLE_COPY } from '@/helpers/tenant';
import { TIMITOMI_APP_URL, TIMITOMI_URL } from '@/lib/constants';
import { leaveGiftPage } from '@/lib/gift-page';
import type { IllustrationName } from '@/lib/illustrations';
import { SUPPORT_EMAIL } from '@/lib/env';
import { NOT_FOUND_TITLE } from '@/lib/seo';
import type { Tenant } from '@/state/tenant';

const ERROR_404_LABEL = 'Error 404';
const ADMIN_LOGIN_URL = `${TIMITOMI_APP_URL}/login`;
const ADMIN_SIGNUP_URL = `${TIMITOMI_APP_URL}/signup`;

export type PageStateTenant = Pick<Tenant, 'name' | 'logo' | 'support_email'>;

const OpenAdminCard = ({ description }: { description: string }) => (
  <PageStateOwnerCard
    icon={KeyRound}
    title="Is this your studio?"
    description={description}
    href={ADMIN_LOGIN_URL}
    linkLabel="Open admin"
  />
);

export const UnknownAddressState = ({
  host,
  claimable,
}: {
  host: string | null;
  claimable: boolean;
}) => (
  <PageState
    illustration="location-off-3d"
    label={ERROR_404_LABEL}
    title="No studio at this address"
    description="Check the spelling of the link, or ask the studio for the correct booking address."
    actions={
      <>
        <Button
          size="lg"
          fullWidth
          onClick={() => {
            window.location.href = TIMITOMI_URL;
          }}
        >
          Go to timitomi.com
        </Button>
        {SUPPORT_EMAIL && (
          <Button
            size="lg"
            variant="secondary-gray"
            fullWidth
            onClick={() => {
              window.location.href = `mailto:${SUPPORT_EMAIL}`;
            }}
          >
            Contact support
          </Button>
        )}
      </>
    }
    aside={
      claimable && host ? (
        <PageStateOwnerCard
          icon={Store}
          title="Want this address for your studio?"
          description={`${host} is free to claim.`}
          href={ADMIN_SIGNUP_URL}
          linkLabel="Create account"
        />
      ) : undefined
    }
  />
);

const StudioHeader = ({ tenant }: { tenant: PageStateTenant }) => (
  <div className="mb-8 flex items-center gap-2.5">
    <Avatar src={tenant.logo ?? undefined} name={tenant.name} size="md" />
    <span className="font-display text-lg font-bold text-ground-heading">
      {tenant.name}
    </span>
  </div>
);

export const MissingPageState = ({ tenant }: { tenant?: PageStateTenant }) => {
  const router = useRouter();

  return (
    <PageState
      illustration="compass-3d"
      label={ERROR_404_LABEL}
      header={tenant && <StudioHeader tenant={tenant} />}
      title={NOT_FOUND_TITLE}
      description="The link may be old, or the class or package is no longer on sale."
      actions={
        <>
          <Button
            size="lg"
            fullWidth
            onClick={() => router.push('/')}
          >
            See the schedule
          </Button>
          {tenant?.support_email && (
            <Button
              size="lg"
              variant="secondary-gray"
              fullWidth
              onClick={() => {
                window.location.href = `mailto:${tenant.support_email}`;
              }}
            >
              Contact the studio
            </Button>
          )}
        </>
      }
    />
  );
};

export const NotLiveState = () => (
  <PageState
    illustration="calendar-blank-3d"
    title={BOOKING_PAGE_UNAVAILABLE_COPY['not-live'].title}
    description={BOOKING_PAGE_UNAVAILABLE_COPY['not-live'].description}
    aside={<OpenAdminCard description="Sign in to publish the booking page." />}
  />
);

export const SuspendedState = () => (
  <PageState
    illustration="lock-3d"
    title={BOOKING_PAGE_UNAVAILABLE_COPY.suspended.title}
    description={BOOKING_PAGE_UNAVAILABLE_COPY.suspended.description}
    aside={<OpenAdminCard description="Sign in to your admin for details." />}
  />
);

export const UnreachableState = () => (
  <PageState
    illustration="plug-3d"
    title={BOOKING_PAGE_UNAVAILABLE_COPY.unreachable.title}
    description={BOOKING_PAGE_UNAVAILABLE_COPY.unreachable.description}
    actions={
      <Button
        size="lg"
        variant="secondary-gray"
        fullWidth
        onClick={() => window.location.reload()}
      >
        Try again
      </Button>
    }
  />
);

export const CrashState = ({ onRetry }: { onRetry: () => void }) => (
  <PageState
    illustration="cone-3d"
    title="Something went wrong"
    description="We have been notified and are fixing it."
    actions={
      <Button size="lg" fullWidth onClick={onRetry}>
        Try again
      </Button>
    }
  />
);

const GIFT_CODE_STATES: Record<
  GiftCodeClosedState,
  { illustration: IllustrationName; askStudio: boolean }
> = {
  NOT_FOUND: { illustration: 'compass-3d', askStudio: false },
  REDEEMED: { illustration: 'check-3d', askStudio: false },
  USED_UP: { illustration: 'check-3d', askStudio: false },
  EXPIRED: { illustration: 'hourglass-3d', askStudio: true },
  DEACTIVATED: { illustration: 'lock-3d', askStudio: true },
};

export const GiftCodeState = ({
  state,
  tenant,
}: {
  state: GiftCodeClosedState;
  tenant?: PageStateTenant;
}) => {
  const { illustration, askStudio } = GIFT_CODE_STATES[state];
  const { title, description } = GIFT_CODE_STATE_COPY[state];
  const contact = tenant?.support_email && (
    <Button
      size="lg"
      variant={askStudio ? undefined : 'secondary-gray'}
      fullWidth
      onClick={() => {
        window.location.href = `mailto:${tenant.support_email}`;
      }}
    >
      Contact the studio
    </Button>
  );

  return (
    <PageState
      illustration={illustration}
      header={tenant && <StudioHeader tenant={tenant} />}
      title={title}
      description={description}
      actions={
        <>
          {askStudio && contact}
          <Button
            size="lg"
            variant={askStudio && contact ? 'secondary-gray' : undefined}
            fullWidth
            onClick={() => leaveGiftPage('/classes')}
          >
            Book a class
          </Button>
          {!askStudio && contact}
        </>
      }
    />
  );
};

export const GiftCodeLoadErrorState = ({
  onRetry,
}: {
  onRetry: () => void;
}) => (
  <PageState
    illustration="plug-3d"
    title="We couldn't open your gift card"
    description="Check your connection and try again."
    actions={
      <Button size="lg" fullWidth onClick={onRetry}>
        Try again
      </Button>
    }
  />
);
